import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Alert } from "@material-ui/lab";
import React, { Fragment, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { useDeleteCandidateAttachment } from "../../api/deleteCandidateAttachment";
import { TCandidateAttachment } from "../../types/candidateAttachment.types";

const StyledTitle = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1rem;
`;

const StyledLabel = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1rem;
  font-weight: 400;
  text-align: center;
  margin: 1rem;
`;

const StyledAlert = styled(Alert)`
  margin: 0 1rem;
`;

const StyledDialogActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 1rem 0.5rem 0.5rem;
`;

const StyledActionButton = styled(Button)`
  margin: 0.5rem;
  flex-grow: 1;
`;

interface IDeleteCandidateAttachmentButtonProps {
  candidateId: string;
  attachment: TCandidateAttachment;
}

/**
 * ECO-1416: deletes a candidate attachment after confirmation. Only rendered for
 * promote managers; the backend enforces it as well.
 */
const DeleteCandidateAttachmentButton = (
  props: IDeleteCandidateAttachmentButtonProps
) => {
  const { candidateId, attachment } = props;

  const { formatMessage } = useIntl();
  const [isConfirming, setIsConfirming] = useState(false);
  const deleteMutation = useDeleteCandidateAttachment();

  const handleDeleteClick = () => {
    deleteMutation.reset();
    setIsConfirming(true);
  };

  const handleCancelClick = () => {
    setIsConfirming(false);
  };

  const handleConfirmClick = () => {
    deleteMutation.mutate(
      { candidateId, attachmentId: attachment.id },
      {
        onSuccess: () => {
          setIsConfirming(false);
        },
      }
    );
  };

  const isDeleting = deleteMutation.isLoading;

  return (
    <Fragment>
      <IconButton
        size="small"
        color="primary"
        aria-label={formatMessage({
          id: "DeleteCandidateAttachmentButton.Eliminar documento",
          defaultMessage: "Eliminar documento",
        })}
        onClick={handleDeleteClick}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      <Dialog
        open={isConfirming}
        onClose={isDeleting ? undefined : handleCancelClick}
        className="MuiCuideo-DeleteCandidateAttachmentDialog"
        fullWidth
        maxWidth="sm"
      >
        <StyledTitle>
          <FormattedMessage
            id="DeleteCandidateAttachmentButton.Eliminar documento"
            defaultMessage="Eliminar documento"
          />
        </StyledTitle>
        <StyledLabel>
          <FormattedMessage
            id="DeleteCandidateAttachmentButton.¿Seguro que quieres eliminar el documento «{title}»? Esta acción no se puede deshacer"
            defaultMessage="¿Seguro que quieres eliminar el documento «{title}»? Esta acción no se puede deshacer"
            values={{ title: <b>{attachment.title ?? ""}</b> }}
          />
        </StyledLabel>
        {deleteMutation.isError && (
          <StyledAlert severity="error">
            <FormattedMessage
              id="DeleteCandidateAttachmentButton.No se ha podido eliminar el documento"
              defaultMessage="No se ha podido eliminar el documento"
            />
          </StyledAlert>
        )}
        <StyledDialogActions>
          <StyledActionButton
            color="primary"
            variant="text"
            onClick={handleCancelClick}
            disabled={isDeleting}
            fullWidth
          >
            <FormattedMessage
              id="DeleteCandidateAttachmentButton.Cancelar"
              defaultMessage="Cancelar"
            />
          </StyledActionButton>
          <StyledActionButton
            color="primary"
            variant="contained"
            onClick={handleConfirmClick}
            disabled={isDeleting}
            fullWidth
          >
            {isDeleting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <FormattedMessage
                id="DeleteCandidateAttachmentButton.Eliminar"
                defaultMessage="Eliminar"
              />
            )}
          </StyledActionButton>
        </StyledDialogActions>
      </Dialog>
    </Fragment>
  );
};

export default DeleteCandidateAttachmentButton;
